// auditpm-v7
// All times in minutes from midnight
function hhmm(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}${String(m).padStart(2, '0')}`
}
function range(start, end) {
  return `${hhmm(start)}-${hhmm(end)}`
}
function addMins(base, mins) { return base + mins }

// Internal tokens replaced at render time
const A1 = '__A1__'   // Auditor 1
const A2 = '__A2__'   // Auditor 2
const ALL = '__ALL__' // Both auditors
const AEE = "Auditee\'s Representative"

function dayHeader(day) {
  return { time: `DAY ${day}`, activity: '', by: '', isDayHeader: true }
}

const OPENING = (s, end) => ({
  time: range(s, end),
  activity: 'Opening Meeting\n  Introduction and roles of audit team\n  Brief on audit criteria, scope, audit objectives\n  Brief on audit methodology\n  Brief on confidentiality and sampling of evidence',
  by: ALL
})
const AUDITEE_INTRO = {
  time: '',
  activity: "  Introduction on organisation key process, personnel etc\n  Update on clients' risk management system and previous audit action plan status, if any\n  Visitor safety induction, if any",
  by: AEE
}
const CLOSING = (s, end) => ({
  time: range(s, end),
  activity: 'Closing Meeting\n  - Brief on audit scope, criteria, objectives\n  - Audit findings\n  - Brief on confidentiality, sampling and feedback mechanism\n  - Highlight consequence if no action taken for imminent danger, if any',
  by: ALL
})
const END = (t) => ({ time: hhmm(t), activity: 'End of Audit', by: ALL })
const LUNCH = (s, end) => ({ time: range(s, end), activity: 'Lunch Break', by: '' })
const TOUR = (s, end) => ({ time: range(s, end), activity: 'Familiarisation Tour', by: A1 })

export const AUDIT_STRUCTURES = ['Parallel', 'Split']
export const HALF_DAY_POSITIONS = ['Day1', 'Day2']            // which day holds the half-day (1 auditor, 1.5 md)
export const SECOND_AUDITOR_BLOCKS = ['Morning', 'Afternoon'] // 2nd auditor half-day block (2 auditors, 1.5 md, same day)

// Explicit, template-driven selection. Inputs are the planning decisions — NOT inferred from man-days.
//   inputs: { auditors, mandays, locations, daySpan, halfDayPosition, secondAuditorBlock }
// Returns { planType, twoDay } for a supported combination, or { blocked:true, reason } when none applies.
export function selectTemplate({ auditors, mandays, locations, daySpan, halfDayPosition, secondAuditorBlock } = {}) {
  const aud = parseInt(auditors) || 1
  const md = parseFloat(mandays) || 0.5
  const loc = parseInt(locations) || 1
  const span = parseInt(daySpan) || 1
  const NO = reason => ({ blocked: true, reason })

  if (aud === 1) {
    if (md <= 0.5) return { planType: loc >= 2 ? 'half_single_2loc' : 'half_single', twoDay: false }
    if (md <= 1)   return { planType: loc >= 2 ? 'full_single_2loc' : 'full_single', twoDay: false }
    if (md <= 1.5) return { planType: halfDayPosition === 'Day1' ? 'oneaud_15_half1' : 'oneaud_15_half2', twoDay: true }
    if (md <= 2)   return { planType: 'oneaud_2full', twoDay: true }
    return NO('No approved auto-template for 1 auditor beyond 2 man-days.')
  }
  // 2 auditors
  if (md < 1) return NO('No approved auto-template for 2 auditors below 1.0 man-day. Use 1 auditor, or confirm man-days.')
  if (md <= 1) {
    // 1.0 md = each auditor a half day, SAME day (never a 2-day plan):
    //   Split (span>=2)  → Auditor 1 morning, Auditor 2 afternoon (twohalf_split)
    //   Parallel         → both auditors jointly on the same half-day; AM/PM comes from the start time
    return span >= 2 ? { planType: 'twohalf_split', twoDay: false } : { planType: 'parallel_half', twoDay: false }
  }
  if (span >= 2) return { planType: md >= 2 ? 'split_full' : 'split_half', twoDay: true }
  if (md <= 1.5) return { planType: secondAuditorBlock === 'Afternoon' ? 'onehalf_two_pm' : 'onehalf_two', twoDay: false }
  if (md <= 2)   return { planType: 'two_two', twoDay: false }
  return NO('No approved auto-template for 2 auditors beyond 2 man-days.')
}

// Normalise activity text: trim each line (removes stray leading indentation, e.g. the
// "  Introduction" double-space), collapse accidental double spaces, and cap blank lines.
export function normalizeText(s) {
  if (typeof s !== 'string') return s
  return s
    .split('\n')
    .map(line => line.replace(/[ \t]+/g, ' ').trim())
    .join('\n')
    .replace(/\n{3,}/g, '\n\n')
    .trim()
}

// Replace __A1__ / __A2__ / __ALL__ tokens with real auditor names — in EVERY string field
// (by, parallelBy, and any token left in activity text) so no "__A2__" artifacts can leak through.
// Unnamed slots get stand-ins ("Auditor 1" / "Auditor 2"); a one-auditor plan never gets "Auditor 2".
export function injectAuditorNames(rows, auditor1Name, auditor2Name, auditorCount = 1) {
  const a1 = (auditor1Name || 'Auditor 1').trim()
  const a2 = auditor2Name ? auditor2Name.trim() : (auditorCount >= 2 ? 'Auditor 2' : null)
  const all = a2 ? `${a1} / ${a2}` : a1

  const resolve = (val) => {
    if (typeof val !== 'string') return val
    return val.replace(/__A1__/g, a1).replace(/__A2__/g, a2 || a1).replace(/__ALL__/g, all)
  }

  return rows.map(row => {
    const out = { ...row, by: resolve(row.by) }
    if (row.activity !== undefined) out.activity = normalizeText(resolve(row.activity))
    if (row.parallelActivity !== undefined) out.parallelActivity = normalizeText(resolve(row.parallelActivity))
    if (row.parallelBy !== undefined) out.parallelBy = resolve(row.parallelBy)
    return out
  })
}

// Swap auditor names in the schedule's "By" fields (by / parallelBy) ONLY — times and activity text are never
// touched. `from` = the names as last saved [auditor 1, auditor 2]; `to` = the current names. The stand-ins
// "Auditor 1" / "Auditor 2" / "All auditors" always map to the current auditors. Each old name keeps its slot
// (old auditor 2's rows go to the new auditor 2, or to auditor 1 when there is no longer a second auditor).
// A name that matches neither (typed by hand, e.g. the client's staff) is kept. Returns the SAME array when
// nothing changed, so callers can tell a real change from a no-op; with no current auditor, nothing changes.
export function remapAuditorNames(rows, { from = [], to = [] } = {}) {
  const n1 = (to[0] || '').trim()
  const n2 = (to[1] || '').trim()
  if (!n1 || !Array.isArray(rows)) return rows
  const key = s => String(s).trim().toLowerCase()
  const map = new Map()
  const put = (name, value) => { if (name && String(name).trim() && !map.has(key(name))) map.set(key(name), value) }
  put(from[0], n1); put(from[1], n2 || n1)          // saved names first, so a slot swap maps correctly
  put('Auditor 1', n1); put('Auditor 2', n2 || n1)
  const all = n2 ? `${n1} / ${n2}` : n1

  const swap = (val) => {
    if (typeof val !== 'string' || !val.trim()) return val
    if (key(val) === 'all auditors') return all
    const parts = val.split(/\s*\/\s*/)
    if (!parts.some(p => map.has(key(p)))) return val
    const names = []
    for (const p of parts) {
      const mapped = map.has(key(p)) ? map.get(key(p)) : p.trim()
      if (!names.some(n => key(n) === key(mapped))) names.push(mapped)
    }
    return names.join(' / ')
  }

  let changed = false
  const next = rows.map(row => {
    if (!row || row.isDayHeader) return row
    const by = swap(row.by)
    const parallelBy = swap(row.parallelBy)
    if (by === row.by && parallelBy === row.parallelBy) return row
    changed = true
    return { ...row, by, ...(row.parallelBy !== undefined ? { parallelBy } : {}) }
  })
  return changed ? next : rows
}

// Builds the raw token rows for a base layout (single-auditor templates + the dedicated parallel
// templates). Names are injected and timing is shifted in generateAuditPlanRows below.
function buildPlanRows(planType, startMinutes, pmStart) {
  const s = startMinutes
  let rows

  switch (planType) {

    case 'half_single': {
      const open = s, tour = addMins(open, 15), doc = addMins(tour, 15)
      const site = addMins(doc, 60), i1 = addMins(site, 30)
      const i2 = addMins(i1,15), i3 = addMins(i2,15), i4 = addMins(i3,15)
      const i5 = addMins(i4,15), i6 = addMins(i5,15), i7 = addMins(i6,15)
      const close = addMins(i7, 15), end = addMins(close, 15)
      rows = [
        OPENING(open, tour), AUDITEE_INTRO, TOUR(tour, doc),
        { time: range(doc, site), activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control\n(4) Implementation\n(5) Review\n\nAudit Highlights', by: A1 },
        { time: range(site, i1), activity: 'Site inspection', by: A1 },
        { time: range(i1, i2), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(i2, i3), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(i3, i4), activity: 'Interview of Implementation person', by: A1 },
        { time: range(i4, i5), activity: 'Interview of RM team leader', by: A1 },
        { time: range(i5, i6), activity: 'Interview of worker 1', by: A1 },
        { time: range(i6, i7), activity: 'Interview of worker 2', by: A1 },
        { time: range(i7, close), activity: 'Interview of worker 3', by: A1 },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'half_single_2loc': {
      const open = s, tour = addMins(open, 15), doc = addMins(tour, 15)
      const travel = addMins(doc, 45), site2 = addMins(travel, 30)
      const i1 = addMins(site2,15), i2 = addMins(i1,15), i3 = addMins(i2,15)
      const i4 = addMins(i3,15), i5 = addMins(i4,15), i6 = addMins(i5,15)
      const i7 = addMins(i6,15), close = addMins(i7, 15), end = addMins(close, 15)
      rows = [
        OPENING(open, tour), AUDITEE_INTRO, TOUR(tour, doc),
        { time: range(doc, travel), activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control\n(4) Implementation\n(5) Review\n\nAudit Highlights', by: A1 },
        { time: range(travel, site2), activity: 'Travel to Site 2', by: ALL },
        { time: range(site2, i1), activity: 'Site inspection — Location 2', by: A1 },
        { time: range(i1, i2), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(i2, i3), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(i3, i4), activity: 'Interview of Implementation person', by: A1 },
        { time: range(i4, i5), activity: 'Interview of RM team leader', by: A1 },
        { time: range(i5, i6), activity: 'Interview of worker 1', by: A1 },
        { time: range(i6, i7), activity: 'Interview of worker 2', by: A1 },
        { time: range(i7, close), activity: 'Interview of worker 3', by: A1 },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'full_single': {
      const open = s, tour = addMins(open, 45), doc1 = addMins(tour, 60)
      const site1 = addMins(doc1, 75), lunch = addMins(site1, 60)
      const doc2 = addMins(lunch, 60), i1 = addMins(doc2, 85)
      const i2 = addMins(i1,20), i3 = addMins(i2,20), i4 = addMins(i3,20)
      const i5 = addMins(i4,20), i6 = addMins(i5,20), i7 = addMins(i6,20)
      const close = addMins(i7, 20), end = addMins(close, 15)
      rows = [
        OPENING(open, tour), AUDITEE_INTRO, TOUR(tour, doc1),
        { time: range(doc1, site1), activity: '(1) Policy\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control', by: A1 },
        { time: range(site1, lunch), activity: 'Site inspection', by: A1 },
        LUNCH(lunch, doc2),
        { time: range(doc2, i1), activity: '(2) Preparation for hazard identification, risk assessment and risk control\n(4) Implementation\n(5) Review\n\nAudit Highlights', by: A1 },
        { time: range(i1, i2), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(i2, i3), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(i3, i4), activity: 'Interview of Implementation person', by: A1 },
        { time: range(i4, i5), activity: 'Interview of RM team leader', by: A1 },
        { time: range(i5, i6), activity: 'Interview of worker 1', by: A1 },
        { time: range(i6, i7), activity: 'Interview of worker 2', by: A1 },
        { time: range(i7, close), activity: 'Interview of worker 3', by: A1 },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'full_single_2loc': {
      const open = s, tour = addMins(open, 45), doc1 = addMins(tour, 60)
      const site1 = addMins(doc1, 75), travelLunch = addMins(site1, 30)
      const loc2start = addMins(travelLunch, 90), doc2 = addMins(loc2start, 60)
      const site2 = addMins(doc2, 25)
      const i1 = addMins(site2,20), i2 = addMins(i1,20), i3 = addMins(i2,20)
      const i4 = addMins(i3,20), i5 = addMins(i4,20), i6 = addMins(i5,20)
      const i7 = addMins(i6,20), close = addMins(i7, 15), end = addMins(close, 15)
      rows = [
        OPENING(open, tour), AUDITEE_INTRO, TOUR(tour, doc1),
        { time: range(doc1, site1), activity: '(1) Policy\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control', by: A1 },
        { time: range(site1, travelLunch), activity: 'Site inspection — Location 1', by: A1 },
        { time: range(travelLunch, loc2start), activity: 'Lunch Break / Travel to Site 2', by: ALL },
        { time: range(loc2start, doc2), activity: '(2) Preparation for hazard identification, risk assessment and risk control\n(4) Implementation  (5) Review  Audit Highlights', by: A1 },
        { time: range(doc2, site2), activity: 'Site inspection — Location 2', by: A1 },
        { time: range(site2, i1), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(i1, i2), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(i2, i3), activity: 'Interview of Implementation person', by: A1 },
        { time: range(i3, i4), activity: 'Interview of RM team leader', by: A1 },
        { time: range(i4, i5), activity: 'Interview of worker 1', by: A1 },
        { time: range(i5, i6), activity: 'Interview of worker 2', by: A1 },
        { time: range(i6, i7), activity: 'Interview of worker 3', by: A1 },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'onehalf_one': {
      const d1open = s, d1tour = addMins(d1open, 45), d1doc1 = addMins(d1tour, 60)
      const d1site = addMins(d1doc1, 75), d1lunch = addMins(d1site, 60)
      const d1doc2 = addMins(d1lunch, 60), d1i1 = addMins(d1doc2, 85)
      const d1i2 = addMins(d1i1,20), d1i3 = addMins(d1i2,20)
      const d1close = addMins(d1i3, 20), d1end = addMins(d1close, 15)
      const d2open = s, d2doc = addMins(d2open, 15)
      const d2i1 = addMins(d2doc, 60), d2i2 = addMins(d2i1,20)
      const d2i3 = addMins(d2i2,20), d2i4 = addMins(d2i3,20)
      const d2close = addMins(d2i4, 20), d2end = addMins(d2close, 15)
      rows = [
        dayHeader(1),
        OPENING(d1open, d1tour), AUDITEE_INTRO, TOUR(d1tour, d1doc1),
        { time: range(d1doc1, d1site), activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control', by: A1 },
        { time: range(d1site, d1lunch), activity: 'Site inspection', by: A1 },
        LUNCH(d1lunch, d1doc2),
        { time: range(d1doc2, d1i1), activity: '(4) Implementation\nAudit Highlights', by: A1 },
        { time: range(d1i1, d1i2), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(d1i2, d1i3), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(d1i3, d1close), activity: 'Interview of Implementation person', by: A1 },
        CLOSING(d1close, d1end), END(d1end),
        dayHeader(2),
        OPENING(d2open, d2doc),
        { time: range(d2doc, d2i1), activity: '(5) Review\n\nAudit Highlights', by: A1 },
        { time: range(d2i1, d2i2), activity: 'Interview of RM team leader', by: A1 },
        { time: range(d2i2, d2i3), activity: 'Interview of worker 1', by: A1 },
        { time: range(d2i3, d2i4), activity: 'Interview of worker 2', by: A1 },
        { time: range(d2i4, d2close), activity: 'Interview of worker 3', by: A1 },
        CLOSING(d2close, d2end), END(d2end),
      ]
      break
    }

    case 'onehalf_two': {
      // Shared opening + tour, then a parallel block (Lead vs Auditor), lunch, a second
      // parallel block, then lead-only interviews, then shared closing/end.
      const open = s, openEnd = addMins(open, 45)
      const tour = openEnd, tourEnd = addMins(tour, 45)
      const par1 = tourEnd, par1End = addMins(par1, 150)        // 1030-1300
      const lunch = par1End, lunchEnd = addMins(lunch, 60)      // 1300-1400
      const par2 = lunchEnd, par2End = addMins(par2, 60)        // 1400-1500
      const v1 = par2End, v2 = addMins(v1,20), v3 = addMins(v2,20), v4 = addMins(v3,20)
      const v5 = addMins(v4,20), v6 = addMins(v5,20), v7 = addMins(v6,20)
      const close = addMins(v7, 20), end = addMins(close, 40)
      rows = [
        OPENING(open, openEnd), AUDITEE_INTRO, TOUR(tour, tourEnd),
        { time: 'PARALLEL_HEADER' },
        { time: range(par1, par1End), parallel: true,
          activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control',
          parallelActivity: '(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control' },
        LUNCH(lunch, lunchEnd),
        { time: range(par2, par2End), parallel: true,
          activity: '(4) Implementation\n(5) Review\n\nAudit Highlights',
          parallelActivity: 'Site Inspection' },
        { time: range(v1, v2), parallel: true, activity: 'Interview of Implementation person / manager', parallelActivity: '' },
        { time: range(v2, v3), parallel: true, activity: 'Interview of Implementation person / supervisor', parallelActivity: '' },
        { time: range(v3, v4), parallel: true, activity: 'Interview of Implementation person', parallelActivity: '' },
        { time: range(v4, v5), parallel: true, activity: 'Interview of RM team leader', parallelActivity: '' },
        { time: range(v5, v6), parallel: true, activity: 'Interview of worker 1', parallelActivity: '' },
        { time: range(v6, v7), parallel: true, activity: 'Interview of worker 2', parallelActivity: '' },
        { time: range(v7, close), parallel: true, activity: 'Interview of worker 3', parallelActivity: '' },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'onehalf_two_pm': {
      // 1.5 md, 2 auditors, same day — second auditor's half-day block is the AFTERNOON
      // (Risk Assessment + Site Inspection after lunch). Mirrors the "2PM" approved reference.
      const open = s, openEnd = addMins(open, 45)
      const tour = openEnd, tourEnd = addMins(tour, 45)
      const m1 = tourEnd, m1End = addMins(m1, 15)         // 1030-1045 Policy
      const m2 = m1End, m2End = addMins(m2, 135)          // 1045-1300 Preparation
      const lunch = m2End, lunchEnd = addMins(lunch, 60)  // 1300-1400
      const par = lunchEnd, parEnd = addMins(par, 60)     // 1400-1500 parallel
      const v1 = parEnd, v2 = addMins(v1,20), v3 = addMins(v2,20), v4 = addMins(v3,20)
      const v5 = addMins(v4,20), v6 = addMins(v5,20), v7 = addMins(v6,20)
      const close = addMins(v7, 20), end = addMins(close, 40)
      rows = [
        OPENING(open, openEnd), AUDITEE_INTRO, TOUR(tour, tourEnd),
        { time: 'PARALLEL_HEADER' },
        { time: range(m1, m1End), parallel: true, activity: '(1) Policy', parallelActivity: '' },
        { time: range(m2, m2End), parallel: true, activity: '(2) Preparation for hazard identification, risk assessment and risk control', parallelActivity: '' },
        LUNCH(lunch, lunchEnd),
        { time: range(par, parEnd), parallel: true,
          activity: '(4) Implementation\n(5) Review\n\nAudit Highlights',
          parallelActivity: '(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control' },
        { time: range(v1, v2), parallel: true, activity: 'Interview of Implementation person / manager', parallelActivity: '' },
        { time: range(v2, v3), parallel: true, activity: 'Interview of Implementation person / supervisor', parallelActivity: '' },
        { time: range(v3, v4), parallel: true, activity: 'Interview of Implementation person', parallelActivity: '' },
        { time: range(v4, v5), parallel: true, activity: 'Interview of RM team leader', parallelActivity: '' },
        { time: range(v5, v6), parallel: true, activity: 'Interview of worker 1', parallelActivity: 'Site Inspection' },
        { time: range(v6, v7), parallel: true, activity: 'Interview of worker 2', parallelActivity: '' },
        { time: range(v7, close), parallel: true, activity: 'Interview of worker 3', parallelActivity: '' },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'two_one': {
      const d1open = s, d1tour = addMins(d1open, 45), d1doc1 = addMins(d1tour, 60)
      const d1site = addMins(d1doc1, 75), d1lunch = addMins(d1site, 60)
      const d1doc2 = addMins(d1lunch, 60), d1i1 = addMins(d1doc2, 85)
      const d1i2 = addMins(d1i1,20), d1i3 = addMins(d1i2,20), d1i4 = addMins(d1i3,20)
      const d1close = addMins(d1i4, 20), d1end = addMins(d1close, 15)
      const d2open = s, d2tour = addMins(d2open, 30), d2doc = addMins(d2tour, 60)
      const d2site = addMins(d2doc, 60), d2lunch = addMins(d2site, 60)
      const d2impl = addMins(d2lunch, 60)
      const d2i1 = addMins(d2impl, 85), d2i2 = addMins(d2i1,20), d2i3 = addMins(d2i2,20)
      const d2i4 = addMins(d2i3,20), d2i5 = addMins(d2i4,20), d2i6 = addMins(d2i5,20)
      const d2close = addMins(d2i6, 20), d2end = addMins(d2close, 15)
      rows = [
        dayHeader(1),
        OPENING(d1open, d1tour), AUDITEE_INTRO, TOUR(d1tour, d1doc1),
        { time: range(d1doc1, d1site), activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control', by: A1 },
        { time: range(d1site, d1lunch), activity: 'Site inspection', by: A1 },
        LUNCH(d1lunch, d1doc2),
        { time: range(d1doc2, d1i1), activity: '(4) Implementation\nAudit Highlights', by: A1 },
        { time: range(d1i1, d1i2), activity: 'Interview of Implementation person / manager', by: A1 },
        { time: range(d1i2, d1i3), activity: 'Interview of Implementation person / supervisor', by: A1 },
        { time: range(d1i3, d1i4), activity: 'Interview of Implementation person', by: A1 },
        { time: range(d1i4, d1close), activity: 'Interview of RM team leader', by: A1 },
        CLOSING(d1close, d1end), END(d1end),
        dayHeader(2),
        OPENING(d2open, d2tour),
        TOUR(d2tour, d2doc),
        { time: range(d2doc, d2site), activity: '(5) Review\n\nAudit Highlights', by: A1 },
        { time: range(d2site, d2lunch), activity: 'Site inspection', by: A1 },
        LUNCH(d2lunch, d2impl),
        { time: range(d2impl, d2i1), activity: 'Document review — additional areas', by: A1 },
        { time: range(d2i1, d2i2), activity: 'Interview of worker 1', by: A1 },
        { time: range(d2i2, d2i3), activity: 'Interview of worker 2', by: A1 },
        { time: range(d2i3, d2i4), activity: 'Interview of worker 3', by: A1 },
        { time: range(d2i4, d2i5), activity: 'Interview of worker 4', by: A1 },
        { time: range(d2i5, d2i6), activity: 'Interview of worker 5', by: A1 },
        { time: range(d2i6, d2close), activity: 'Interview of worker 6', by: A1 },
        CLOSING(d2close, d2end), END(d2end),
      ]
      break
    }

    case 'two_two': {
      const open = s, openEnd = addMins(open, 15)
      const tour = openEnd, tourEnd = addMins(tour, 60)        // 0915-1015
      const par1 = tourEnd, par1End = addMins(par1, 60)        // 1015-1115
      const par2 = par1End, par2End = addMins(par2, 75)        // 1115-1230
      const discuss1 = par2End, discuss1End = addMins(discuss1, 30) // 1230-1300
      const lunch = discuss1End, lunchEnd = addMins(lunch, 60) // 1300-1400
      const par3 = lunchEnd, par3End = addMins(par3, 195)      // 1400-1715
      const discuss2 = par3End, discuss2End = addMins(discuss2, 15)  // 1715-1730
      const close = addMins(discuss2End, 15), end = addMins(close, 15) // 1745-1800, 1800
      rows = [
        OPENING(open, openEnd), AUDITEE_INTRO, TOUR(tour, tourEnd),
        { time: 'PARALLEL_HEADER' },
        { time: range(par1, par1End), parallel: true,
          activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control',
          parallelActivity: '(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control' },
        { time: range(par2, par2End), parallel: true,
          activity: '(4) Implementation',
          parallelActivity: 'Site Inspection' },
        { time: range(discuss1, discuss1End), activity: 'Auditors Internal Discussion', by: ALL },
        LUNCH(lunch, lunchEnd),
        { time: range(par3, par3End), parallel: true,
          activity: '(5) Review\n\nAudit Highlights',
          parallelActivity: 'Interview of Implementation person / manager\nInterview of Implementation person / supervisor\nInterview of Implementation person\nInterview of RM team leader\nInterview of worker 1\nInterview of worker 2\nInterview of worker 3' },
        { time: range(discuss2, discuss2End), activity: 'Auditors Internal Discussion', by: ALL },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'parallel_half': {
      // 2 auditors, 1.0 md, SAME half-day — split responsibilities across two parallel tracks
      // (mirrors the firm's approved Excel reference): Auditor 1 covers Policy / Preparation /
      // Implementation / Review + Audit Highlights then the interviews, while Auditor 2 covers
      // Risk Assessment then the Site Inspection concurrently. Joint opening, tour, internal
      // discussion and closing. Times derive from the start time (0900 → ends 1300).
      const open = s, tour = addMins(open, 15)
      const par1 = addMins(tour, 15), par1End = addMins(par1, 90)      // 0930-1100 checklist split
      const par2 = par1End, par2End = addMins(par2, 90)                // 1100-1230 interviews ∥ site
      const discuss = par2End, discussEnd = addMins(discuss, 15)       // 1230-1245
      const close = discussEnd, end = addMins(close, 15)               // 1245-1300
      rows = [
        OPENING(open, tour), AUDITEE_INTRO,
        { time: range(tour, par1), activity: 'Familiarisation Tour', by: ALL },
        { time: 'PARALLEL_HEADER' },
        { time: range(par1, par1End), parallel: true,
          activity: '(1) Policy\n(2) Preparation for hazard identification, risk assessment and risk control\n(4) Implementation\n(5) Review\n\nAudit Highlights',
          parallelActivity: '(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control' },
        { time: range(par2, par2End), parallel: true,
          activity: 'Interview of Implementation person / manager\nInterview of Implementation person / supervisor\nInterview of Implementation person\nInterview of RM team leader\nInterview of worker 1\nInterview of worker 2\nInterview of worker 3',
          parallelActivity: 'Site Inspection' },
        { time: range(discuss, discussEnd), activity: 'Auditors Internal Discussion', by: ALL },
        CLOSING(close, end), END(end),
      ]
      break
    }

    case 'twohalf_split': {
      // 2 auditors, 1.0 md — ONE continuous day. Shared opening, familiarisation tour and closing;
      // Auditor 1 runs the morning (anchored at s), Auditor 2 the afternoon (anchored at pmStart). The
      // afternoon can never start before the morning ends; if there is a gap, a lunch break fills it.
      const open = s, tour = addMins(open, 45), doc1 = addMins(tour, 60)
      const site1 = addMins(doc1, 75), amEnd = addMins(site1, 60)   // end of Auditor 1's morning
      const pm = (pmStart != null && pmStart >= amEnd) ? pmStart : addMins(amEnd, 60) // Auditor 2 afternoon start
      const i1 = addMins(pm, 85)
      const i2 = addMins(i1,20), i3 = addMins(i2,20), i4 = addMins(i3,20)
      const i5 = addMins(i4,20), i6 = addMins(i5,20), i7 = addMins(i6,20)
      const close = addMins(i7, 20), end = addMins(close, 15)
      rows = [
        OPENING(open, tour), AUDITEE_INTRO, TOUR(tour, doc1),
        { time: range(doc1, site1), activity: '(1) Policy\n(3) Risk Assessment: Hazard identification, Risk Evaluation and Risk Control', by: A1 },
        { time: range(site1, amEnd), activity: 'Site inspection', by: A1 },
        ...(pm > amEnd ? [LUNCH(amEnd, pm)] : []),
        { time: range(pm, i1), activity: '(2) Preparation for hazard identification, risk assessment and risk control\n(4) Implementation\n(5) Review\n\nAudit Highlights', by: A2 },
        { time: range(i1, i2), activity: 'Interview of Implementation person / manager', by: A2 },
        { time: range(i2, i3), activity: 'Interview of Implementation person / supervisor', by: A2 },
        { time: range(i3, i4), activity: 'Interview of Implementation person', by: A2 },
        { time: range(i4, i5), activity: 'Interview of RM team leader', by: A2 },
        { time: range(i5, i6), activity: 'Interview of worker 1', by: A2 },
        { time: range(i6, i7), activity: 'Interview of worker 2', by: A2 },
        { time: range(i7, close), activity: 'Interview of worker 3', by: A2 },
        CLOSING(close, end), END(end),
      ]
      break
    }

    default:
      return buildPlanRows('half_single', startMinutes)
  }

  return rows
}

// Assign a whole day's "by" to one auditor (used by split-by-days). No within-day task movement.
const reassignBy = (rows, token) => rows.map(r => (r.by === A1 || r.by === ALL) ? { ...r, by: token } : r)

// Template-driven generation: compose the approved day blocks at each day's start time, then ONLY
// inject names and (for split) set each day's owning auditor. No task reassignment or rebalancing.
//   opts: { day1Start, day2Start, a1, a2, auditors }  (start minutes; day2Start used by 2-day plans;
//          auditors = how many are on the plan, so an unnamed one-auditor plan never shows "Auditor 2")
export function generateAuditPlanRows(planType, opts = {}) {
  const { day1Start = 9 * 60, day2Start = 9 * 60, a1, a2, auditors = 1 } = opts
  let rows
  switch (planType) {
    case 'oneaud_15_half1': // 1 auditor, 2 days: half day on Day 1, full day on Day 2
      rows = [dayHeader(1), ...buildPlanRows('half_single', day1Start), dayHeader(2), ...buildPlanRows('full_single', day2Start)]; break
    case 'oneaud_15_half2': // 1 auditor, 2 days: full day on Day 1, half day on Day 2
      rows = [dayHeader(1), ...buildPlanRows('full_single', day1Start), dayHeader(2), ...buildPlanRows('half_single', day2Start)]; break
    case 'oneaud_2full':    // 1 auditor, 2 full days
      rows = [dayHeader(1), ...buildPlanRows('full_single', day1Start), dayHeader(2), ...buildPlanRows('full_single', day2Start)]; break
    case 'split_half':      // 2 auditors, 2 days: Day 1 full (Auditor 1), Day 2 half (Auditor 2)
      rows = [dayHeader(1), ...reassignBy(buildPlanRows('full_single', day1Start), A1), dayHeader(2), ...reassignBy(buildPlanRows('half_single', day2Start), A2)]; break
    case 'split_full':      // 2 auditors, 2 days: Day 1 full (Auditor 1), Day 2 full (Auditor 2)
      rows = [dayHeader(1), ...reassignBy(buildPlanRows('full_single', day1Start), A1), dayHeader(2), ...reassignBy(buildPlanRows('full_single', day2Start), A2)]; break
    case 'parallel_half':   // 2 auditors, 1.0 md, same half-day: dedicated SPLIT-track template (Auditor 1
                            // checklist+interviews ∥ Auditor 2 risk assessment+site inspection) — no longer
                            // the joint single-track ("both names on every row") layout.
      rows = buildPlanRows('parallel_half', day1Start); break
    case 'twohalf_split':   // 2 auditors, 1.0 md: one continuous day — Auditor 1 morning, Auditor 2 afternoon
      rows = buildPlanRows('twohalf_split', day1Start, day2Start); break
    default:                // all single-day templates
      rows = buildPlanRows(planType, day1Start)
  }
  return injectAuditorNames(rows, a1, a2, auditors)
}

export function parseStartTime(timeStr) {
  const clean = String(timeStr).replace(':', '')
  const padded = clean.padStart(4, '0')
  const h = parseInt(padded.slice(0, 2))
  const m = parseInt(padded.slice(2, 4))
  return h * 60 + m
}

export function formatTimeDisplay(minutes) {
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  const period = h >= 12 ? 'PM' : 'AM'
  const h12 = h > 12 ? h - 12 : h === 0 ? 12 : h
  return `${h12}:${String(m).padStart(2, '0')} ${period}`
}